import { Component, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { getValue, translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClipboardService } from 'ngx-clipboard';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { Constants } from 'src/app/models/constants';
import { RouterSegments } from 'src/app/models/router-segments';
import { InstancesService } from 'src/app/services/api/instances.service';
import { PrivateEndpointsService } from 'src/app/services/api/private-endpoints-service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SystemService } from 'src/app/services/api/system.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ErrorPageService } from 'src/app/services/util/error-page.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { UrlService } from 'src/app/services/util/url.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import cloneDeep from 'lodash/cloneDeep';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { Types } from 'src/app/models/service/schedule';
import { SchedulerService } from 'src/app/services/api/scheduler.service';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import { ConfigurationDetailsComponent } from 'src/app/components/configuration-details/configuration-details.component';
import { ConfigurationApplicationStatus } from 'src/app/models/configurations/configuration-state';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { DeploymentClass } from 'src/app/models/instance-class';
import { PermissionDialogComponent } from 'src/app/components/permission-dialog/permission-dialog.component';
import { Permissions, Datazone, Attributes } from 'src/app/models/principal/permission';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { InstanceEnvironment } from 'src/app/models/instance-environment';
import { PathToLiveEnvironment } from 'src/app/models/blueprints/path-to-live-environment';
import { ExternalPackageDetailsComponent } from '../../external-package/external-package-details/external-package-details.component';
import { DisasterRecoveryService } from 'src/app/services/api/disaster-recovery.service';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.INSTANCE_START_DATAZONE_NONPII,
    action: Permissions.INSTANCE_START,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.INSTANCE_STOP_DATAZONE_NONPII,
    action: Permissions.INSTANCE_STOP,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.INSTANCE_START_DATAZONE_PII,
    action: Permissions.INSTANCE_START,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.INSTANCE_STOP_DATAZONE_PII,
    action: Permissions.INSTANCE_STOP,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  }
];

@Component({
    selector: 'app-tenant-create-schedule',
    templateUrl: './tenant-create-schedule.component.html',
    styleUrls: ['./tenant-create-schedule.component.scss'],
    standalone: false
})
export class TenantCreateScheduleComponent  implements OnInit  {
  private destroyRef = inject(DestroyRef);

  cronHeader: any = [
    {
      label: 'One Time',
      value: 'once',
      data: 'one_time',
      disabled: false
    },
    {
      label: 'Daily',
      value: 'daily',
      data: 'cron',
      disabled: false
    },
    {
      label: 'Weekly',
      value: 'weekly',
      data: 'cron',
      disabled: false
    },
    {
      label: 'Monthly',
      value: 'monthly',
      data: 'cron',
      disabled: false
    }
  ];
  monthOptions: any = [
    {
      label: 'Days',
      value: 'days'
    },
    {
      label: 'On',
      value: 'on'
    }
  ];
  types: any = [
    {
      name: Types.START_INSTANCE,
      value: 'start'
    },
    {
      name: Types.STOP_INSTANCE,
      value: 'stop'
    },
    {
      name: Types.APPLY_RELEASE_PACKAGE,
      value: 'apply'
    },
    {
      name: Types.SOLUTION_REFRESH,
      value: 'refresh'
    }
  ];
  days: any = [
    {
      name: 'All',
      value: '*',
      checked: false
    },
    {
      name: 'Sunday',
      value: 'SUN',
      checked: false
    },
    {
      name: 'Monday',
      value: 'MON',
      checked: false
    },
    {
      name: 'Tuesday',
      value: 'TUE',
      checked: false
    },
    {
      name: 'Wednesday',
      value: 'WED',
      checked: false
    },
    {
      name: 'Thursday',
      value: 'THU',
      checked: false
    },
    {
      name: 'Friday',
      value: 'FRI',
      checked: false
    },
    {
      name: 'Saturday',
      value: 'SAT',
      checked: false
    },
  ];
  months: any = [
    {
      name: 'All',
      value: '*',
      checked: false
    },
    {
      name: 'Jan',
      value: 'JAN',
      checked: false
    },
    {
      name: 'Feb',
      value: 'FEB',
      checked: false
    },
    {
      name: 'Mar',
      value: 'MAR',
      checked: false
    },
    {
      name: 'Apr',
      value: 'APR',
      checked: false
    },
    {
      name: 'May',
      value: 'MAY',
      checked: false
    },
    {
      name: 'Jun',
      value: 'JUN',
      checked: false
    },
    {
      name: 'Jul',
      value: 'JUL',
      checked: false
    },
    {
      name: 'Aug',
      value: 'AUG',
      checked: false
    },
    {
      name: 'Sep',
      value: 'SEP',
      checked: false
    },
    {
      name: 'Oct',
      value: 'OCT',
      checked: false
    },
    {
      name: 'Nov',
      value: 'NOV',
      checked: false
    },
    {
      name: 'Dec',
      value: 'DEC',
      checked: false
    },
  ];

  selectedFilter: any =  {
    label: 'One Time',
    value: 'once',
    data: 'one_time'
  };
  selectedMonthFilter: any =  {
    label: 'Days',
    value: 'days'
  };
  numbers: number[] = [];
  onDaysOptions: any = [
    {name: 'First',value: '#1',checked: false},
    {name: 'Second',value: '#2',checked: false},
    {name: 'Third',value: '#3',checked: false},
    {name: 'Fourth',value: '#4',checked: false},
    {name: 'Last',value: 'L',checked: false},
  ];
  onSpecificDaysOptions: any = [
    { name: 'Sunday', value: 'SUN', checked: false },
    { name: 'Monday', value: 'MON', checked: false },
    { name: 'Tuesday', value: 'TUE', checked: false },
    { name: 'Wednesday', value: 'WED', checked: false },
    { name: 'Thursday', value: 'THU', checked: false },
    { name: 'Friday', value: 'FRI', checked: false },
    { name: 'Saturday', value: 'SAT', checked: false }
  ];
  detailsForm: FormGroup;
  activityForm: FormGroup;
  occurenceForm: FormGroup;
  breadcrumbItems: BreadcrumbItem[] = [];
  drInProgress$: Observable<boolean>;
  isDREnabled$: Observable<boolean>;
  showDRBanner$: Observable<boolean>;
  tenantId: any;
  visitedSteps = {
    stepOne: true,
    stepTwo: false,
    stepThree: false,
    stepFour: false
  };
  stepsArray = [1, 2, 3, 4];
  currentStep = 1;
  Constants = Constants;
  blueprints: any;
  selectedType: any;
  selectedBlueprint: any;
  instances: any;
  selectedInstance: any;
  filteredInstances: any;
  filteredBlueprints: any;
  filteredTypes: any;
  minDate: Date;
  minEndDate: Date;
  startDateOption: any;
  hideEndTime: boolean;
  recurEvery: boolean;
  showDays: boolean;
  showMonths: boolean;
  daysSelect: boolean;
  onSelect: boolean;
  selectedOnSpecificDay: any;
  selectedOnDay: any;
  cronValue: any;
  weekDaysSelected: string;
  monthCountSelected: string;
  scheduleInstance: any;
  selectedData: any;
  scheduleId: any;
  url: boolean;
  instanceData: any;
  startTime: string;
  endTime: string;
  searchValueBp: any;
  searchValueType: any;
  searchValueInstance: any;
  showReleasePack: boolean;
  searchValueRelease: any;
  filteredReleases: any;
  selectedRelease: any;
  configurations: any;
  availableConfigurations: any;
  permissionsMap: { [key: string]: boolean };
  notAuthorized = false;
  solutionVersionData: any;
  versionData = [];
  versionsList = [];
  selectedSolutionVersion: any;
  isVersionAvailable = true;
  dropdownLoader: boolean;
  addExternalOption = false;
  searchValueSR = undefined;
  showSolutionRefresh: boolean;
  externalSelected: any;
  activeSchedules: any = [];

  constructor(private activatedRoute: ActivatedRoute, public authService: AuthService, private instanceService: InstancesService,
      private sidenavService: SidenavService, private tenantService: TenantService, private systemService: SystemService,
      private solutionsService: SolutionsService, private toastrService: CustomToastrService, private router: Router,
      private clipboardApi: ClipboardService, public urlService: UrlService, private permissionsService: PermissionsService,
       private dialog: MatDialog, private errorPageService: ErrorPageService, public formBuilder: FormBuilder, private blueprintsService: BlueprintsService,
      public datepipe: DatePipe, private schedulerService: SchedulerService, private configurationsService: ConfigurationsService, private disasterRecoveryService: DisasterRecoveryService ){

      this.numbers = Array.from({ length: 31 }, (_, i) => i + 1);
      this.drInProgress$ = this.disasterRecoveryService.drInProgress$;
      this.isDREnabled$ = this.disasterRecoveryService.isDREnabled$;
    }

    ngOnInit(): void {
      this.showDRBanner$ = combineLatest([this.drInProgress$, this.isDREnabled$]).pipe(
        map(([drInProgress, isDREnabled]) => drInProgress === true && isDREnabled === true)
      );
      this.url = this.router.url.endsWith('update');
      this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
      this.tenantId = this.activatedRoute.snapshot.params.id;
      this.scheduleId = this.activatedRoute.snapshot.params.scheduleId;
      this.filteredTypes = this.types;
      this.getSchedules();
      this.blueprintsService.getBlueprints(this.tenantId).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(blueprint => {
              this.blueprints = blueprint;
              this.filteredBlueprints = blueprint;
              if (this.activatedRoute.snapshot.routeConfig?.path === RouterSegments.CREATE + '/' + RouterSegments.TYPE) {
                this.scheduleInstance = JSON.parse(localStorage.getItem('scheduleInstance'));
                if (this.scheduleInstance) {
                  const type = this.types.find((item) => item.name === this.scheduleInstance[0]?.instanceType);
                  const blueprint = this.blueprints?.find(item => item.name === this.scheduleInstance[0]?.blueprintName);
                  this.activityForm.get('type').setValue(type);
                  this.activityForm.get('blueprint').setValue(blueprint);
                  this.typeSelected(type);
                  this.blueprintSelected(blueprint);
                } else {
                  this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , this.tenantId, RouterSegments.SCHEDULE, RouterSegments.CREATE]);
                }
              } else {
                localStorage.removeItem('scheduleInstance');
              }
              if(this.url) {
              this.getSelectedScheduler();
              }
            });
      this.breadcrumbItems.push({ icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.tenantId] },
        { label: translate('TENANT_LAYOUT.MENU.SCHEDULE'), url: [RouterSegments.TENANT, this.tenantId, RouterSegments.SCHEDULE] },
        { label: 'create' });
            this.detailsForm = this.formBuilder.group({
              name: ["", Validators.required],
              description: ["", Validators.required]
            });
            this.activityForm = this.formBuilder.group({
              instance: ['', [Validators.required]],
              type: ['', [Validators.required]],
              blueprint: ['', [Validators.required]]
            });
            this.occurenceForm = this.formBuilder.group({
              startDate: ['', [Validators.required]],
              monthDays: [''],
              sdHours: ['0'],
              edHours: ['0'],
              sdMinutes: ['0'],
              edMinutes: ['0'],
              recurDays: ['1']
            });

            this.minDate = new Date();
            this.minEndDate = new Date();
            this.minEndDate.setDate(this.minDate.getDate() + 1);
            this.daysSelect = true;
    }

    // Method to get the scheduler details for update.
    getSelectedScheduler(): void {
      this.schedulerService.getScheduleById(this.tenantId, this.scheduleId).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(schedule => {
        this.selectedData = schedule;
          if(this.url && this.selectedData) {
            this.editScheduler();
          }
        },
        (error) => {
          this.editScheduler();
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
      }

      // Occurrence tab click change
    filterOptionChange(option): void {
      this.selectedFilter = option;
      this.hideEndTime = true;
      this.recurEvery = false;
      this.showDays = false;
      this.showMonths = false;
      if(option.value === 'daily') {
        this.occurenceForm.addControl('endDate', new FormControl('', [Validators.required]));
        this.occurenceForm.get('endDate').setValue(this.selectedData?.endTime);
        this.recurEvery = true;
      } else if(option.value === 'weekly') {
        this.occurenceForm.addControl('endDate', new FormControl('', [Validators.required]));
        this.occurenceForm.get('endDate').setValue(this.selectedData?.endTime);
        this.showDays = true;
      } else if(option.value === 'monthly') {
        this.occurenceForm.addControl('endDate', new FormControl('', [Validators.required]));
        this.occurenceForm.get('endDate').setValue(this.selectedData?.endTime);
        this.showMonths = true;
      } else if(option.value === 'once') {
        this.occurenceForm.removeControl('endDate');
        this.hideEndTime = false;
      }
    }

    //Occurrence tab month option click.
    filterMonthOptionChange(option): void {
      this.selectedMonthFilter = option;
      if(option.value === 'days') {
        this.occurenceForm.removeControl('onDays');
        this.occurenceForm.removeControl('onSpecificDays');
        this.daysSelect = true;
        this.onSelect = false;
      } else {
        this.occurenceForm.addControl('onDays', new FormControl('', [Validators.required]));
        this.occurenceForm.addControl('onSpecificDays', new FormControl('', [Validators.required]));
        this.occurenceForm.get('onSpecificDays').setValue(this.onSpecificDaysOptions.find(item => item.checked));
        this.occurenceForm.get('onDays').setValue(this.onDaysOptions.find(item => item.checked));
        this.daysSelect = false;
        this.onSelect = true;
      }
    }

    // Method to patch the values to its corresponding formcontrol.
    editScheduler(): void {
      let recurDaysValue;
      let monthDaysValue;
      let cronArray;
      if(this.selectedData?.frequency !== 'one_time') {
      cronArray = this.selectedData?.cronExpression.split(" ");
      if(cronArray[3].includes('/')) {
        recurDaysValue = cronArray[3].split('/');
      }
      if(cronArray[3].includes(',')) {
        monthDaysValue = cronArray[3].split(',');
        monthDaysValue = monthDaysValue.map(Number);
      }
      if(cronArray[3].length === 1 && cronArray[3] !== '*') {
        monthDaysValue = [parseInt(cronArray[3])];
      }
      if(cronArray[4] !== '*') {
        const monthDays = cronArray[4].split(',');
        monthDays.forEach(day => {
          this.months.find(item => {
            if(item.value === day) {
              item.checked = true;
            }
          })
        })
        this.monthCountSelected = monthDays.toString();
      }
      if(cronArray[5]) {
        if(cronArray[5].includes('L') || cronArray[5].includes('#')) {
          if(cronArray[5].includes('L')) {
            const cronValue = cronArray[5].split('L');
            this.onDaysOptions[4].checked = true;
            this.onSpecificDaysOptions.find(item => {
              if(item.value === cronValue[0]) {
                item.checked = true;
              }
            })
          } else {
            const cronValue = cronArray[5].split('#');
            this.onSpecificDaysOptions.find(item => {
              if(item.value === cronValue[0]) {
                item.checked = true;
              }
            })
            this.onDaysOptions.find(item => {
              if(item.value.split('#')[1] === cronValue[1]) {
                item.checked = true;
              }
            })
          }
        }
        else {
          let weeksDays = [];
          weeksDays = cronArray[5].split(',');
          weeksDays.forEach(day => {
            this.days.find(item => {
              if(item.value === day) {
                item.checked = true;
              }
            })
          })
          this.weekDaysSelected = weeksDays.toString();
        }
      }
    }
      this.detailsForm.patchValue({
        name: this.selectedData?.name.split('-') ? this.selectedData?.name.split('-')[0] : this.selectedData?.name,
        description: this.selectedData?.description
      });
      this.occurenceForm.patchValue({
        monthDays: monthDaysValue,
        recurDays: recurDaysValue ? recurDaysValue[1] : ['1']
      });
      const blueprintData = this.filteredBlueprints?.find(item => item.id === this.selectedData?.metadata?.blueprintId);
      if(blueprintData){
        this.blueprintSelected(blueprintData);
      }
      const typeData = this.selectedData?.name.includes('start') ? this.types[0] : this.selectedData?.name.includes('stop') ? this.types[1] : this.selectedData?.name.includes('refresh') ? this.types[3]  : this.types[2];
      if(this.selectedData?.name.includes('apply') || this.selectedData?.name.includes('config')) {
        this.showReleasePack = true;
        this.activityForm.addControl('config', new FormControl('', [Validators.required]));
        this.activityForm.addControl('skipTest', new FormControl());
        this.activityForm.controls.skipTest.disable();
      } else {
        this.showReleasePack = false;
      }
       if(this.selectedData?.name.includes('refresh')) {
        this.showSolutionRefresh = true;
        this.dropdownLoader = true;
        this.activityForm.addControl('buildVersion', new FormControl('', [Validators.required]));
      } else {
        this.showSolutionRefresh = false;
      }
      this.occurenceForm.get('startDate').setValue(this.selectedData?.startTime);
      const startTime = moment(new Date(this.selectedData?.startTime)).utc();
      this.occurenceForm.get('sdHours').setValue(startTime.hours() ? startTime.hours() : ['0']);
      this.occurenceForm.get('sdMinutes').setValue(startTime.minutes() ? startTime.minutes() : ['0']);
      const endTime = moment(new Date(this.selectedData?.endTime)).utc();
      this.occurenceForm.get('edHours').setValue(endTime.hours() ? endTime.hours() : ['0']);
      this.occurenceForm.get('edMinutes').setValue(endTime.minutes() ? endTime.minutes() : ['0']);
      this.activityForm.get('blueprint').setValue(blueprintData);
      this.activityForm.get('type').setValue(typeData);
    }

    // Method gets called whenever we click on the next and previous buttons
    goToStep(currentStep, now?, previous?): void {
      if(now === 1 && !FormUtilService.validateForm(this.detailsForm)) {
          return;
      } else {
        if (this.currentStep === 2) {
          if(this.showReleasePack || this.selectedData?.frequency === 'one_time') {
            this.selectedFilter = this.cronHeader[0];
            this.hideEndTime = false;
            this.cronHeader.forEach(item => {
              if(item.data !== 'one_time') {
                item.disabled = true;
              } else {
                item.disabled = false;
              }
            })
          } else if(this.selectedData && this.selectedData?.frequency !== 'one_time') {
            this.selectedFilter = this.cronHeader[1];
            this.filterOptionChange(this.selectedFilter);
            this.cronHeader.forEach(item => {
              if(item.data === 'one_time') {
                item.disabled = true;
              } else {
                item.disabled = false;
              }
            })
          }
          else if(this.showSolutionRefresh) {
            const maxScheduleReached = this.showMaxScheduleRestriction();
            if (maxScheduleReached) { return;}
            this.cronHeader.forEach(item => {
              if(item.data === 'one_time') {
                item.disabled = false;
              } else {
                item.disabled = true;
              }
            })
          }
           else {
            this.cronHeader.forEach(item => item.disabled = false);
          }
          if(!previous) {
            this.visitedSteps.stepTwo = true;
            if(this.selectedType?.value === 'apply') {
              this.activityForm.controls.skipTest.disable();
            }

            if(!FormUtilService.validateForm(this.activityForm)) {
              if(!this.selectedData && this.selectedType?.value === 'apply') {
                this.activityForm.controls.skipTest.enable();
              }
              return;
            }
          }
        } else if (this.currentStep === 3) {
          this.startTime = this.datepipe.transform(this.occurenceForm.get('startDate').value, 'yyyy-MM-dd') + 'T' + (Number(this.occurenceForm.get('sdHours').value < 10) ? '0' + this.occurenceForm.get('sdHours').value : this.occurenceForm.get('sdHours').value) + ':' +
              (Number(this.occurenceForm.get('sdMinutes').value < 10) ? '0' + this.occurenceForm.get('sdMinutes').value : this.occurenceForm.get('sdMinutes').value) + ':00Z';
            if(this.selectedFilter.value !== 'once') {
              this.endTime = this.datepipe.transform(this.occurenceForm.get('endDate').value, 'yyyy-MM-dd') + 'T' + (Number(this.occurenceForm.get('edHours').value < 10) ? '0' + this.occurenceForm.get('edHours').value : this.occurenceForm.get('edHours').value) + ':' +
                (Number(this.occurenceForm.get('edMinutes').value < 10) ? '0' + this.occurenceForm.get('edMinutes').value : this.occurenceForm.get('edMinutes').value) + ':00Z'
            }
            if(this.showSolutionRefresh){
              const dateNotAllowed = this.scheduleDateRestriction();
              if (dateNotAllowed) { return; }
            }
            if(!previous) {
              this.visitedSteps.stepThree = true;
              if(!FormUtilService.validateForm(this.occurenceForm)) {
                return;
              }
            }
          this.cronFormation();
        } else if (this.currentStep === 4) {
          this.visitedSteps.stepFour = true;
          if(!this.selectedData && this.selectedType?.value === 'apply') {
            this.activityForm.controls.skipTest.enable();
          }
        }
        this.currentStep = currentStep;
      }
    }

    // Method to convert the selection values to cron format manually.
    cronFormation(): void {
      this.cronValue = '0 0 0 * * *';
      const option = this.selectedFilter;
      if(option.value === 'daily') {
        this.cronValue = '0 ' + this.occurenceForm.get('sdMinutes').value.toString() + ' ' + this.occurenceForm.get('sdHours').value.toString() + ' ' +
        (this.occurenceForm.get('recurDays').value ? '1/' + (this.occurenceForm.get('recurDays').value.toString()) : '*') + ' ' +'* ?';
      } else if(option.value === 'weekly') {
        this.cronValue = '0 ' + this.occurenceForm.get('sdMinutes').value.toString() + ' ' + this.occurenceForm.get('sdHours').value.toString() + ' ' +
        '? *' + ' ' + (this.weekDaysSelected ? this.weekDaysSelected : '*');
      } else if(option.value === 'monthly') {
        if(this.selectedMonthFilter.value === 'days') {
          this.cronValue = '0 ' + this.occurenceForm.get('sdMinutes').value.toString() + ' ' + this.occurenceForm.get('sdHours').value.toString() + ' ' +
          (this.occurenceForm.get('monthDays').value ? this.occurenceForm.get('monthDays').value.toString() :'*') + ' ' + (this.monthCountSelected ? this.monthCountSelected : '*') + ' ' + '?';
        } else {
          this.cronValue = '0 ' + this.occurenceForm.get('sdMinutes').value.toString() + ' ' + this.occurenceForm.get('sdHours').value.toString() + ' ' +
        '?' + ' ' + (this.monthCountSelected ? this.monthCountSelected : '*') + ' ' +
        ((this.occurenceForm.get('onSpecificDays') && this.occurenceForm.get('onDays')) ? (this.occurenceForm.get('onSpecificDays').value.value.toString() + this.occurenceForm.get('onDays').value.value.toString()) : '*');
        }
      } else if(option.value === 'once') {
        this.cronValue = '0 ' + this.occurenceForm.get('sdMinutes').value.toString() + ' ' + this.occurenceForm.get('sdHours').value.toString() + ' ' + '* * *';
      }
    }

    // Method to go back to scheduler page.
    goToScheduler(): void {
      this.router.navigate(['/',this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.SCHEDULE]);
    }

    // Method gets called when click on the type dropdown.
    typeSelected(type): void {
      this.activityForm.get('instance').setValue(undefined);
      if(type.value === 'apply') {
        this.showReleasePack = true;
        this.showSolutionRefresh = false;
        this.activityForm.addControl('config', new FormControl('', [Validators.required]));
        this.activityForm.addControl('skipTest', new FormControl());
      }
      else {
        this.showSolutionRefresh = false;
        this.selectedRelease = undefined;
        this.showReleasePack = false;
        this.activityForm.removeControl('config');
        this.activityForm.removeControl('skipTest');
        this.activityForm.removeControl('buildVersion');
        if(type.value === 'refresh'){
          this.showSolutionRefresh = true;
        this.activityForm.addControl('buildVersion', new FormControl('', [Validators.required]));
        }
      }
      this.selectedType = type;
    }

    // Method gets called when click on the blueprint dropdown.
    blueprintSelected(blueprint): void {
      this.selectedBlueprint = blueprint;
      this.selectedInstance = undefined;
      this.instances = undefined;
      this.filteredInstances = undefined;
      this.selectedRelease = undefined;
      this.filteredReleases = undefined;
      this.activityForm.get('config')?.reset();
      this.activityForm.get('instance')?.reset();
      this.activityForm.get('buildVersion')?.reset();

      this.instanceService.getInstancesForBlueprint(this.selectedBlueprint?.id).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((instance: any) => {
        instance.data.forEach(element => {
          if (element?.deploymentClass === DeploymentClass.E) {
            return;
          }
          if (element?.deploymentClass === DeploymentClass.D) {
            element.environment = 'DEV';
          } else {
              element.environment = this.selectedBlueprint?.pathToLiveConfiguration
              .find(item => item?.instanceId === element?.id)?.environment;
          }
          this.configurationsService.getConfigurationStates(element.id).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((configurationStates: any) => {
          configurationStates.sort((state1, state2) => state2?.applicationStartTime.getTime() - state1?.applicationStartTime.getTime());
            element.configurationStates = configurationStates;
            if (element.configurationStates?.length > 0) {
              element.latestConfigurationState = element.configurationStates[0];
            }
          })
          this.instances = instance.data.filter((inst: any) => inst?.deploymentClass !== DeploymentClass.E);
          this.filteredInstances = this.instances;
        }, (error) => {
            this.instances = instance.data.filter((inst: any) => inst?.deploymentClass !== DeploymentClass.E);
            this.filteredInstances = this.instances;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
          });
        if (this.scheduleInstance?.length) {
          const instance = this.instances?.find(item => item.name === this.scheduleInstance[0]?.instanceName);
          this.activityForm.get('instance').setValue(instance);
          this.instanceSelected(instance);
        }
        if(this.selectedData){
          this.instanceData = this.filteredInstances.find(item => item.id === this.selectedData?.metadata.instanceId);
          this.activityForm.get('instance').setValue(this.instanceData);
          this.instanceSelected(this.instanceData);
        }
      },
      (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    }

  // Method gets called when click on the instance dropdown.
    instanceSelected(instance): void {
      this.notAuthorized = false;
      const invalidStatuses = [DeployStatus.TERMINATION_FAILED,  DeployStatus.TERMINATING,  DeployStatus.TERMINATED];
      const isInvalidStatus = invalidStatuses.includes(instance?.status);

      if (isInvalidStatus) {
        this.notAuthorized = true;
      } else if (this.selectedType?.name === Types.STOP_INSTANCE && !this.permissionsMap[PermissionKeys.INSTANCE_STOP_DATAZONE_NONPII] && instance.dataZone === DataZone.DEVELOPMENT) {
        this.notAuthorized = true;
      } else if (this.selectedType?.name === Types.STOP_INSTANCE && !this.permissionsMap[PermissionKeys.INSTANCE_STOP_DATAZONE_PII] && instance.dataZone === DataZone.SECURE) {
        this.notAuthorized = true;
      } else if (this.selectedType?.name === Types.START_INSTANCE && !this.permissionsMap[PermissionKeys.INSTANCE_START_DATAZONE_NONPII] && instance.dataZone === DataZone.DEVELOPMENT) {
        this.notAuthorized = true;
      } else if (this.selectedType?.name === Types.START_INSTANCE && !this.permissionsMap[PermissionKeys.INSTANCE_START_DATAZONE_PII] && instance.dataZone === DataZone.SECURE) {
        this.notAuthorized = true;
      } else if ( instance.dataZone === DataZone.LIVE) {
        this.notAuthorized = true;
      }
      if (this.notAuthorized) {
        this.dialog.open(PermissionDialogComponent, {
          panelClass: 'default-dialog-wrapper',
          width: '566px',
          autoFocus: false,
          data: {
            title: translate('SCHEDULE_DETAILS.TITLE'),
            message: isInvalidStatus ? translate('SCHEDULE_DETAILS.INVALID_STATE') : translate('SCHEDULE_DETAILS.NO_PERMISSION'),
            name: instance?.name
          }
        })
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(confirm => {
            if (confirm) {
            }
          });
      } else {
        this.selectedInstance = instance;
        this.selectedRelease = undefined;
        this.configurations = undefined;
        this.filteredReleases = undefined;
        this.activityForm.get('config')?.reset();
        this.activityForm.get('buildVersion')?.reset();
        this.configurationsService.getConfigurations(this.selectedBlueprint?.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((configurations: any)=>{
        this.configurations = configurations;
        this.availableConfigurations = this.getAvailableConfigurations(instance);
          this.filteredReleases = this.availableConfigurations;
          if(this.selectedData && this.selectedData?.metadata?.configurationId) {
            const releaseData = this.filteredReleases?.find(item => item.id === this.selectedData?.metadata?.configurationId);
            this.activityForm.get('config').setValue(releaseData);
            if(this.selectedData?.metadata?.skipTest === 'true') {
            this.activityForm.get('skipTest').setValue(true);
            } else {
              this.activityForm.get('skipTest').setValue(false);
            }
          }
          if (this.scheduleInstance && this.scheduleInstance[0]?.configPackage) {
            const releaseData = this.configurations?.find(item => item.id === this.scheduleInstance[0].configPackage.id);
            this.activityForm.get('config').setValue(releaseData);
            this.releaseSelected(releaseData);
          }
          this.addExternalOption = false;
          this.externalSelected = false;
          if (this.selectedData?.metadata?.packageReferenceId) {
            this.addExternalOption = true;
            this.externalSelected = true;
          }
        },
        (error) => {
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
        })
          this.versionData = [];
          this.versionsList = [];
         if(this.selectedType?.value === 'refresh' || (this.url && this.selectedData?.metadata?.targetSolutionVersionId)) {
         this.solutionsService
          .getSolutionVersion(this.selectedInstance.solutionId, this.selectedInstance.solutionVersionId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(
            version => {
              this.selectedInstance.version = version.version;
              this.selectedInstance.releaseVersion = version.releaseVersion;
            },
            error => {
              setTimeout(() => {
                const title = translate('ERRORS.SERVICE_AVAILABILITY');
                const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
                this.toastrService.errMessage(message, title);
              }, 500);
            });
            if (this.selectedInstance?.environment === PathToLiveEnvironment.PREPROD) {
              this.selectedInstance.preprodData = this.selectedInstance;
            }
          this.solutionsService.getSolutionVersions(this.selectedInstance.solutionId).pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(data => {
                this.solutionVersionData = [...data];
                  if (data) {
                      data.forEach(element => {
                        this.versionData.push(element);
                      });

                      if (this.selectedInstance?.version) {
                        let versions = [];
                        let arr = [];
                        data.forEach(element => {
                          arr.push(element.version);
                        });
                        arr = arr.map( a => a.split('.').map( n => +n+100000 ).join('.') ).sort()
                                        .map( a => { const parts = a.split('.').map( n => +n-100000)
                                        const major = parts[0];
                                        const minor = parts[1] ? parts[1].toString().padStart(2, '0') : '00';
                                        const patch = parts[2] ? parts[2].toString().padStart(2, '0') : '00';
                                        return `${major}.${minor}.${patch}`;
                       });
                        let isGreater = false;
                        arr?.forEach(item => {
                          if(isGreater) {
                            this.versionData.forEach(data => {
                              if (this.selectedInstance.environment === PathToLiveEnvironment.LIVE && this.dataVersion(data?.version) === item && this.dataVersion(this.selectedInstance.preprodData?.version) ===  item){
                                versions = [...versions, data];
                              }
                              if (this.dataVersion(data?.version) === item && this.selectedInstance.environment !== PathToLiveEnvironment.LIVE) {
                                versions = [...versions, data];
                              }
                          })
                          }
                          if (item === this.dataVersion(this.selectedInstance?.version)) {
                            isGreater = true;
                          }
                        })
                        // this.versionData?.forEach(item => {
                        //   if (Number(item?.version.split(".").join('')) > Number(this.selectedInstance?.version.split(".").join(''))) {
                        //     versions = [...versions, item];
                        //   }
                        // })
                        this.versionData = this.versionData.map(item => item);
                        if (this.url && this.selectedData?.metadata?.targetSolutionVersionId){
                          this.versionGroup(this.versionData);
                          return;
                        }
                        if (this.selectedInstance?.status === 'update_failed') {
                          versions.push(this.selectedInstance);
                        }
                        if (versions?.length) {
                          this.versionGroup(versions);
                        }
                      }
                      // this.versionData.sort((a, b) => {
                      //   return b.version.localeCompare(a.version);
                      // });
                      this.dropdownLoader = true;
                      this.showSolutionRefresh = true;
                  }
                if(this.versionData.length === 0) {
                  const noRelease = 'Release not Available';
                  this.isVersionAvailable = false;
                  this.versionData.push(noRelease);
                }
              },
              (error) => {
                setTimeout(() => {
                  const title = translate('ERRORS.SERVICE_AVAILABILITY');
                  const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
                  this.toastrService.errMessage(message, title);
                }, 500);
              });
            }
      }
    }

    /**
       *Method to check for the ptl workflow before apply release package applies.
    */
    private getAvailableConfigurations(instance): any {
        const requiredEnvironment = this.selectedBlueprint?.pathToLiveConfiguration.find(
          ptl => ptl.environment === instance?.environment && ptl.instanceId === instance.id)?.requires;
          let availableConfigurations = this.configurations;
        // Only configurations which are created before (or equal) latest successfully applised configuration on previous environment
        const prodInstance = this.filteredInstances?.find(instances => instances.id === this.selectedInstance.id);
        if (this.selectedInstance?.dataZone === DataZone.LIVE &&
          prodInstance?.configurationStates?.find(config => config.status === ConfigurationApplicationStatus.VERIFIED)) {
          const latestVerifiedInstance = prodInstance.configurationStates
            .find(configurationState => configurationState.status === ConfigurationApplicationStatus.VERIFIED);
          const verifiedInstanceConfig =
            this.getConfigurationById(latestVerifiedInstance.configurationId);
          availableConfigurations = availableConfigurations
            .filter(configuration => configuration.dateCreated === verifiedInstanceConfig.dateCreated);
          } else if (requiredEnvironment) {
          const requiredInstance = this.filteredInstances?.find(ptlInstance => ptlInstance.environment === requiredEnvironment);
          if (requiredInstance?.configurationStates) {
            const latestSuccessfulStateOfRequiredInstance = requiredInstance.configurationStates
              .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
            if (latestSuccessfulStateOfRequiredInstance) {
                      const configAvailable = availableConfigurations;
                      const latestAppliedConfigurationOnRequiredInstance =
                        this.getConfigurationById(latestSuccessfulStateOfRequiredInstance.configurationId);
                        if (requiredEnvironment === InstanceEnvironment.PREPROD) {
                          availableConfigurations = latestAppliedConfigurationOnRequiredInstance ? [latestAppliedConfigurationOnRequiredInstance] : [];
                        } else {
                        availableConfigurations = [];
                        for(const configStates of requiredInstance.configurationStates) {
                          if (configStates.status === ConfigurationApplicationStatus.SUCCESS) {
                            for(const availableConfig of configAvailable) {
                              if(availableConfig.id === configStates.configurationId) {
                                availableConfigurations.push(availableConfig);
                              }
                              availableConfigurations = [...new Set(availableConfigurations)];
                            }
                          }
                        }
                      }
                      const latestVersion = latestAppliedConfigurationOnRequiredInstance?.manifest?.version;
                      if (latestVersion && requiredEnvironment !== InstanceEnvironment.PREPROD) {

                        const [latestMajor, latestMinor] = latestVersion.split('.').map(num => parseInt(num, 10));

                        availableConfigurations = this.configurations.filter(item => {
                          const [major, minor, patch] = item.manifest.version.split('.').map(num => parseInt(num, 10));
                          // version is less than selected major.minor
                          const isLowerVersion = major < latestMajor || (major === latestMajor && minor < latestMinor);
                          // version belongs to same major.minor (hotfixes)
                          const isHotfix = major === latestMajor && minor === latestMinor;
                          return isLowerVersion || isHotfix;
                        });
                      }
                      availableConfigurations.sort((a, b) => {
                        const asort = a.manifest.version.split('.').map(num => parseInt(num, 10));
                        const bsort = b.manifest.version.split('.').map(num => parseInt(num, 10));

                        for (let i = 0; i < 3; i++) {
                          if (asort[i] > bsort[i]) return 1;
                          if (asort[i] < bsort[i]) return -1;
                        }
                        return 0;
                      });
                    } else {
                      availableConfigurations = [];
                    }
                  } else {
                    availableConfigurations = [];
                  }
                }
        if (instance?.configurationStates) {
      const latestSuccessfulState = instance.configurationStates
        .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
      if (latestSuccessfulState) {
        const latestAppliedConfiguration = this.getConfigurationById(latestSuccessfulState.configurationId);
        availableConfigurations = availableConfigurations
          .filter(configuration =>
            this.compareVersions(configuration.version, latestAppliedConfiguration.version) > 0 );
      }
    }

    return availableConfigurations;
  }

  compareVersions(version: string, latestVersion: string): number {
    const version1 = version.split('.').map(num => parseInt(num, 10));
    const version2 = latestVersion.split('.').map(num => parseInt(num, 10));
    for (let i = 0; i < Math.max(version1.length, version2.length); i++) {
      if (version1[i] > version2[i]) return 1;
      if (version1[i] < version2[i]) return -1;
    }
    return 0;
  }

    private getConfigurationById(configurationId: string): any {
        return this.configurations.find(configuration => configuration.id === configurationId);
    }

    // Method gets called when click on the apply release dropdown.
    releaseSelected(release): void {
      this.selectedRelease = release;
    }

    // Occurrence tab specific day option selected.
    specificDayOptionSelected(data): void {
      this.selectedOnSpecificDay = data;
    }

    // Occurrence tab day option selected.
    dayOptionSelected(data): void {
      this.selectedOnDay = data;
    }

    // search the instances by name.
    searchInstances(value): void {
      this.filteredInstances = this.instances.filter(item => item?.name?.toLowerCase().startsWith(value.toLowerCase()));
    }
    // search for blueprints by name.
    searchBlueprints(value): void {
      this.filteredBlueprints = this.blueprints.filter(item => item?.name?.toLowerCase().startsWith(value.toLowerCase()));
    }


    // search the types dropdown by name.
    searchTypes(value): void {
      this.filteredTypes = this.types.filter(item => item?.name?.toLowerCase().startsWith(value.toLowerCase()));
    }
    // search the release package dropdown by name.
    searchApply(value): void {
      this.filteredReleases = this.filteredReleases?.filter(configurations =>
        configurations.manifest.name?.toLocaleLowerCase().includes(value)
        );
    }

     searchSolutionRefresh(value): void {
      this.versionData = this.versionsList.filter(item => item?.version?.toLowerCase().startsWith(value.toLowerCase()));
    }


    // Resets the search data.
    resetFilter(): void {
      this.searchValueBp = undefined;
      this.searchValueType = undefined;
      this.searchValueInstance = undefined;
      this.searchValueRelease = undefined;
      this.searchValueSR = undefined;
      this.filteredInstances = this.instances;
      this.filteredBlueprints = this.blueprints;
      this.filteredTypes = this.types;
      this.filteredReleases = this.availableConfigurations;
      this.versionData = [...this.versionsList];

    }

    // Providing option for enddate selection based on the start date selected bcoz end date should be higher than start date.
    startDateChange(event): void {
      this.startDateOption = event.value;
      const result = cloneDeep(new Date(this.startDateOption));
      result.setDate(result.getDate() + 1);
      this.minEndDate = result;
    }

    // Occurrence tab week days selection with 'All'.
    selectDays(data): void {
      if(data.name === 'All') {
        if(data.checked) {
        this.days.map(item => item.checked = false);
        } else {
          this.days.map(item => item.checked = true);
        }
      } else {
      this.days.find(item => {
        if(item.name === data.name) {
          item.checked = !item.checked;
          const selectedLength = this.days.filter(e => e.checked === true && e.name !== 'All');
          if(selectedLength.length === 7) {
            this.days[0].checked = true;
          } else {
            this.days[0].checked = false;
          }
        }
      })
    }
      let weeksDays = [];
      const data1 = this.days.filter(item => item.checked);
      if(data1.find(item => item.name === 'All')) {
        weeksDays.push('*');
      } else {
        data1.forEach(item => {
          weeksDays.push(item.value);
        })
      }
      this.weekDaysSelected = weeksDays.toString();
    }

     // Occurrence tab months selection with 'All'.
    selectMonths(data): void {
      this.months.find(item => {
        if(data.name === 'All') {
          if(data.checked) {
          this.months.map(item => item.checked = false);
          } else {
            this.months.map(item => item.checked = true);
          }
        }
        else if(item.name === data.name) {
          item.checked = !item.checked;
          const selectedLength = this.months.filter(e => e.checked === true && e.name !== 'All');
          if(selectedLength.length === 12) {
            this.months[0].checked = true;
          } else {
            this.months[0].checked = false;
          }
        }
      })
      let monthsCount = [];
      const data1 = this.months.filter(item => item.checked);
      if(data1.find(item => item.name === 'All')) {
        monthsCount.push('*');
      } else {
        data1.forEach(item => {
          monthsCount.push(item.value);
        })
      }
      this.monthCountSelected = monthsCount.toString();
    }

    // Post call to create schedule
    createSchedule(): void {
      this.dialog.open(ConfirmationDialogComponent, {
            panelClass: 'default-dialog-wrapper',
            width: '566px',
            autoFocus: false,
            data: {
              title: translate('SCHEDULE_DETAILS.TITLE'),
              message: translate('SCHEDULE_DETAILS.MESSAGE'),
              confirmButtonText: translate('BUTTONS.CONFIRM')
            }
          })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(confirm => {
              if (confirm) {
            let data : any;
            if(this.selectedFilter.value !== 'once') {
            data = {
              id: this.selectedInstance.id,
              schedule: {
                name: this.detailsForm.get('name').value,
                description: this.detailsForm.get('description').value,
                frequency: this.selectedFilter.data,
                cronExpression: this.cronValue,
                startTime: this.startTime,
                endTime: this.endTime
              }
            }
          } else {
            data = {
              id: this.selectedInstance.id,
              schedule: {
                name: this.detailsForm.get('name').value,
                description: this.detailsForm.get('description').value,
                frequency: this.selectedFilter.data,
                startTime: this.startTime,
              }
            }
          }
          if(this.activityForm.get('type').value.value === 'start') {
            data.schedule.name = data.schedule.name + '-start';
            this.instanceService.instanceSchedulerStart(data)
              .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(data => {
                  this.goToScheduler();
                }, (error) => {
                  const description = error?.message
                  || error?.error?.message
                  || error?.errmsg
                  || translate('SERVICE.INTERNAL_SERVER_ERROR');
                  setTimeout(() => {
                    this.openErrorDialog(error);
                  }, 500);
                });
            } else if(this.activityForm.get('type').value.value === 'stop'){
              data.schedule.name = data.schedule.name + '-stop';
              this.instanceService.instanceSchedulerStop(data)
              .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(data => {
                  this.goToScheduler();
                }, (error) => {
                  const description = error?.message
                  || error?.error?.message
                  || error?.errmsg
                  || translate('SERVICE.INTERNAL_SERVER_ERROR');
                  setTimeout(() => {
                    this.openErrorDialog(error);
                  }, 500);
                });
            } else if(this.activityForm.get('type').value.value === 'apply') {
              let skipTest: boolean;
              skipTest = (this.activityForm.get('skipTest').value === true) ? false : true;
              if(this.selectedFilter.value !== 'once') {
              data = {
                id: this.selectedRelease.id,
                instanceId: this.selectedInstance.id,
                skipTest: skipTest,
                schedule: {
                  name: this.detailsForm.get('name').value,
                  description: this.detailsForm.get('description').value,
                  frequency: this.selectedFilter.data,
                  cronExpression: this.cronValue,
                  startTime: this.startTime,
                  endTime: this.endTime
                }
              }
            } else {
              data = {
                id: this.selectedRelease.id,
                instanceId: this.selectedInstance.id,
                skipTest: skipTest,
                schedule: {
                  name: this.detailsForm.get('name').value,
                  description: this.detailsForm.get('description').value,
                  frequency: this.selectedFilter.data,
                  startTime: this.startTime,
                }
              }
            }
              data.schedule.name = data.schedule.name + '-config';
               this.configurationsService.applyConfigurationSchedule(data)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                  this.goToScheduler();
                }, (error) => {
                  const description = error?.message
                  || error?.error?.message
                  || error?.errmsg
                  || translate('SERVICE.INTERNAL_SERVER_ERROR');
                  setTimeout(() => {
                    this.openErrorDialog(error);
                  }, 500);
                });
            }

                else if(this.activityForm.get('type').value.value === 'refresh') {
                data = {
                  id: this.selectedInstance.id,
                  targetSolutionVersionId: this.selectedSolutionVersion,
                  schedule: {
                    name: this.detailsForm.get('name').value,
                    description: this.detailsForm.get('description').value,
                    frequency: this.selectedFilter.data,
                    startTime: this.startTime
                  }
                }
                if (this.externalSelected) {
                  data.packageReferenceId = this.selectedInstance?.packageReferenceId;
                }
                  data.schedule.name = data.schedule.name + '-refresh';
                  this.instanceService.solutionUpgradeScheduler(data)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                  this.goToScheduler();
                }, (error) => {
                  const description = error?.message
                  || error?.error?.message
                  || error?.errmsg
                  || translate('SERVICE.INTERNAL_SERVER_ERROR');
                  setTimeout(() => {
                    this.openErrorDialog(error);
                  }, 500);
                });
            }

          }
        })
    }

    // Method to show the view details of release package applied
    configurationNameClick(): void {
      const configuration = this.selectedRelease;
      configuration.instanceName = this.selectedInstance.name;
      this.dialog.open(ConfigurationDetailsComponent, {
        height: '50vh',
        width: '40vw',
        panelClass: 'flex-dialog',
        data: {
          configuration
        }
      });
    }

    // Error block
    openErrorDialog(data): void {
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '630px',
          panelClass: 'flex-dialog',
          data: {
            title: translate('SCHEDULE_DETAILS.SCHEDULE_VALIDATION_ERROR'),
            descriptionError: data
          }
        });
      }

       // Post call to Update schedule
      updateSchedule(): void {
        this.dialog.open(ConfirmationDialogComponent, {
          panelClass: 'default-dialog-wrapper',
          width: '566px',
          autoFocus: false,
          data: {
            title: translate('SCHEDULE_DETAILS.UPDATE_TITLE'),
            message: translate('SCHEDULE_DETAILS.UPDATE_MESSAGE'),
            confirmButtonText: translate('BUTTONS.CONFIRM')
          }
        })
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(confirm => {
            if (confirm) {
              let data;
              if(this.selectedFilter.value !== 'once') {
                data = {
                tenantId: this.tenantId,
                id: this.selectedData?.id,
                  name: this.detailsForm.get('name').value,
                  description: this.detailsForm.get('description').value,
                  cronExpression: this.cronValue,
                  startTime: this.startTime,
                  endTime: this.endTime,
                };
              }
                else {
                data = {
                  tenantId: this.tenantId,
                  id: this.selectedData?.id,
                    name: this.detailsForm.get('name').value,
                    description: this.detailsForm.get('description').value,
                    startTime: this.startTime,
                    endTime: this.endTime,
                };
              }
              if(this.activityForm.get('type').value.value === 'start') {
                data.name = data.name + '-start';
              } else if(this.activityForm.get('type').value.value === 'stop') {
                data.name = data.name + '-stop';
              } else if(this.activityForm.get('type').value.value === 'refresh') {
                data.name = data.name + '-refresh';
              } else {
                data.name = data.name + '-config';
              }
              this.schedulerService.putSchedulerById(data)
                .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe(data => {
                    this.goToScheduler();
                  }, (error) => {
                    const description = error?.message
                    || error?.error?.message
                    || error?.errmsg
                    || translate('SERVICE.INTERNAL_SERVER_ERROR');
                    setTimeout(() => {
                      this.openErrorDialog(error);
                    }, 500);
                  })
            }
          })
      }

    // Method for cancel confirmation
    cancel(): void {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('SCHEDULE_DETAILS.CANCEL_TITLE'),
          message: translate('SCHEDULE_DETAILS.CANCEL_MESSAGE'),
          confirmButtonText: translate('BUTTONS.YES'),
          cancelButtonText: translate('BUTTONS.NO')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
            this.goToScheduler();
          }
        })
    }

    // Method called when dropdown gets closed.
    dropdownClose(): void {
      if (this.filteredTypes?.length === 0) {
        this.filteredTypes = this.types;
      }
      if (this.filteredBlueprints?.length === 0) {
        this.filteredBlueprints = this.blueprints;
      }
      if (this.filteredInstances?.length === 0) {
        this.filteredInstances = this.instances;
      }
      if (this.filteredReleases?.length === 0) {
        this.filteredReleases = this.configurations;
      }
     if (this.versionData?.length === 0) {
      this.versionData = [...this.versionsList];
     }

    }


  dataVersion(version): any {
    const offset = version.split('.').map(n => +n + 100000).join('.');
    const parts = offset.split('.').map(n => +n - 100000);
    const major = parts[0];
    const minor = parts[1]?.toString().padStart(2, '0') || '00';
    const patch = parts[2]?.toString().padStart(2, '0') || '00';
    return `${major}.${minor}.${patch}`;
  }
    versionGroup(versions) {
    let group = [];
    this.versionsList = versions;
    this.versionsList.forEach(item => {
      group.push(item?.version?.split('.')[0]);
    });
    group = [...new Set(group)];

    let makeGrouping = [];
    group.forEach(groupItem => {
      const versions: any = this.versionsList.filter(item => item?.version?.split('.')[0] === groupItem);
      const groupName = {version: groupItem, group: true, releaseVersion: (versions[0]?.releaseVersion.split('.')[0].split('').length > 2 ? versions[0]?.releaseVersion.split('.')[0] : 'R'+versions[0]?.releaseVersion.split('.')[0])};
      versions.push(groupName);
      makeGrouping.push(versions);
    });

    const combineData = makeGrouping.flat();
    const finalData = combineData.sort((a, b) => {
      if (a.version < b.version) return -1;
      if (a.version > b.version) return 1;
    })
    this.versionsList = finalData;
    this.versionData = finalData;
      if (this.url && this.selectedData?.metadata?.targetSolutionVersionId) {
        const versionSelected = this.solutionVersionData?.find(item => item.id === this.selectedData?.metadata?.targetSolutionVersionId);
        this.activityForm.get('buildVersion').setValue(versionSelected);
      }
  }

  getSelectedVersion(event): void {
    this.selectedSolutionVersion = event.id;
      if(this.selectedInstance?.version?.split('.',1).toString() === event?.version?.split('.',1).toString())  {
        this.addExternalOption = true;
      } else {
        this.addExternalOption = false;
      }
    }

    selectExternalTypes(): void {
    if(this.externalSelected) {
      this.externalSelected = false;
    } else {
      this.externalSelected = true;
    }
  }

    externalPackageInfo(): void {
      if (this.selectedInstance.packageReferenceId) {
        this.dialog.open(ExternalPackageDetailsComponent, {
          height: '85vh',
          width: '50vw',
          panelClass: 'custom-pack-dialog',
          data: this.selectedInstance
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
          if (result) {
          }
        });
      }
    }
  
  // fetch schedules
  getSchedules(): void {
    this.schedulerService.getSchedules(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(response => {
      if (response.total > 10) {
        const size = response.total
        this.schedulerService.getSchedules(this.tenantId, size)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(results => {
          results.data = results.data?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          this.activeSchedules = results.data?.filter(item => item.status === 'active' && item.metadata?.targetSolutionVersionId) || []; 
        })
      } else {
        response.data = response.data?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        this.activeSchedules = response.data?.filter(item => item.status === 'active' && item.metadata?.targetSolutionVersionId) || []; 
      }
    },
    (error) => {
      setTimeout(() => {
        const title = translate('ERRORS.SERVICE_AVAILABILITY');
        const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
        this.toastrService.errMessage(message, title);
      }, 500);
    });
  }

  normalizeUtc(dateStr) {
    return dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  }

  // Validates that the selected schedule date is not within +/- 2 days of the latest active solution upgrade schedule for the selected environment.
  scheduleDateRestriction() : boolean {
    const schedules = this.activeSchedules?.filter(schedule => schedule.metadata?.instanceId === this.selectedInstance.id);
    if (!schedules.length) { return false;}
  
    // Get the latest schedule based on startTime
    let latestSchedule:any;
    if (this.url) {
      latestSchedule = schedules.find(item => item.id !== this.selectedData.id);
    } else {
      latestSchedule = schedules.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())[0];
    }
    if (!latestSchedule) { return false;}

    const activeStart = new Date(this.normalizeUtc(latestSchedule.startTime));
    const minDateTime = new Date(activeStart);
    minDateTime.setDate(minDateTime.getDate() - 2);
    const maxDateTime = new Date(activeStart);
    maxDateTime.setDate(maxDateTime.getDate() + 2);
    const selectedDateTime = new Date(this.startTime);

    if (selectedDateTime >= minDateTime && selectedDateTime <= maxDateTime) {
      // show popup
      const currentDate = this.datepipe.transform(activeStart, 'MMM dd yyyy, HH:mm:ss', 'UTC');
      const nextRunDateMax = this.datepipe.transform(new Date(maxDateTime), 'MMM dd yyyy, HH:mm:ss', 'UTC');
      const nextRunDateMin = this.datepipe.transform(new Date(minDateTime), 'MMM dd yyyy, HH:mm:ss', 'UTC');
      this.dialog.open(ErrorDialogComponent, {
        minHeight: '342px',
        width: '630px',
        panelClass: 'flex-dialog',
        data: {
          title: translate('SCHEDULE_DETAILS.SCHEDULE_RESTRICTION.TITLE1'),
          scheduleDescription: translate('SCHEDULE_DETAILS.SCHEDULE_RESTRICTION.MESSAGE1', {
          currentDate: currentDate}),
          coolingPeriod: translate('SCHEDULE_DETAILS.SCHEDULE_RESTRICTION.COOLING_PERIOD', { nextRunDateMin: nextRunDateMin, nextRunDateMax: nextRunDateMax})
        }
      });
      return true;
    }
    return false;
  }

  // Validates that an environment does not have more than two active solution upgrade schedules.
  showMaxScheduleRestriction(): boolean {
    const schedules = this.activeSchedules?.filter(schedule => schedule.metadata?.instanceId === this.selectedInstance.id);
    if (schedules.length >= 2) {
      this.dialog.open(ErrorDialogComponent, {
        width: '600px',
        panelClass: 'flex-dialog',
        data: {
          title: translate('SCHEDULE_DETAILS.SCHEDULE_RESTRICTION.TITLE2'),
          scheduleDescription: translate('SCHEDULE_DETAILS.SCHEDULE_RESTRICTION.MESSAGE2')
        }
      });
      return true;
    }
    return false;
  }
}


